import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Pie, PieChart, Cell, Legend } from 'recharts';
import type { InventoryMetrics } from '@/lib/admin/analytics';

interface StatusDistributionChartProps {
  data: InventoryMetrics;
}

const chartConfig = {
  available: {
    label: 'Available',
    color: 'hsl(197, 24%, 44%)', // brand-teal
  },
  reserved: {
    label: 'Reserved',
    color: 'hsl(197, 30%, 55%)', // champagne-light
  },
  sold: {
    label: 'Sold',
    color: 'hsl(213, 29%, 26%)', // brand-navy
  },
};

export function StatusDistributionChart({ data }: StatusDistributionChartProps) {
  const chartData = [
    {
      name: 'Available',
      value: data.available,
      fill: 'var(--color-available)',
    },
    {
      name: 'Reserved',
      value: data.reserved,
      fill: 'var(--color-reserved)',
    },
    {
      name: 'Sold',
      value: data.sold,
      fill: 'var(--color-sold)',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status Distribution</CardTitle>
        <CardDescription>Breakdown of unit availability status</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer
          config={chartConfig}
          className="h-[300px] w-full"
        >
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <ChartTooltip content={<ChartTooltipContent />} />
            <Legend />
          </PieChart>
        </ChartContainer>
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-status-available">{data.available}</div>
            <div className="text-xs text-muted-foreground">Available</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-status-reserved">{data.reserved}</div>
            <div className="text-xs text-muted-foreground">Reserved</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-status-sold">{data.sold}</div>
            <div className="text-xs text-muted-foreground">Sold</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

